// frontend/src/pages/PasswordResetConfirmPage.js
import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { apiClient } from '../services/api';
import LoadingSpinner from '../components/Common/LoadingSpinner';

const PasswordResetConfirmPage = () => {
  const { uid, token } = useParams(); // Get uid and token from URL
  const navigate = useNavigate();

  const [newPassword1, setNewPassword1] = useState('');
  const [newPassword2, setNewPassword2] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword1 !== newPassword2) {
      setError("Passwords do not match.");
      setMessage('');
      return;
    }
    if (!uid || !token) {
        setError("Invalid password reset link. UID or token is missing.");
        setMessage('');
        return;
    }
    setIsLoading(true);
    setMessage('');
    setError('');

    try {
      // dj_rest_auth's password reset confirm endpoint
      await apiClient.post('auth/password/reset/confirm/', {
        uid,
        token,
        new_password1: newPassword1,
        new_password2: newPassword2,
      });
      setMessage('Your password has been successfully reset! You can now log in with your new password.');
      setTimeout(() => navigate('/login'), 5000); // Redirect to login
    } catch (err) {
      let errorDetail = 'Password reset failed. The link might be invalid or expired, or the password may not meet requirements.';
      if (err.response?.data) {
        const data = err.response.data;
        if (data.new_password2) { // Specific password validation errors
            errorDetail = data.new_password2.join(' ');
        } else if (data.detail) {
            errorDetail = data.detail;
        } else if (Array.isArray(data) && data.length > 0) {
            errorDetail = data.join(' ');
        } else if (typeof data === 'object' && Object.keys(data).length > 0) {
            errorDetail = Object.values(data).flat().join(' ');
        }
      }
      setError(errorDetail);
      console.error("Password reset confirm error:", err.response?.data || err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-var(--navbar-height,88px)-var(--footer-height,88px))] flex flex-col items-center justify-center bg-gradient-to-b from-white to-blue-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-md space-y-8">
         <div className="text-center">
            <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-gray-900">Set Your New Password</h2>
        </div>
        <form onSubmit={handleSubmit} className="space-y-6 bg-white p-8 sm:p-10 shadow-xl rounded-lg border border-gray-200">
          {isLoading && <div className="text-center mb-4"><LoadingSpinner /></div>}
          {message && <p className="text-green-600 text-sm text-center bg-green-100 p-3 rounded-md mb-4">{message}</p>}
          {error && <p className="text-red-600 text-sm text-center bg-red-100 p-3 rounded-md mb-4">{error}</p>}
          
          {!message && ( // Hide form after successful message
            <>
              <div>
                <label htmlFor="new_password1" className="block text-sm font-medium leading-6 text-gray-900">New Password</label>
                <div className="mt-2">
                  <input
                    id="new_password1"
                    name="new_password1"
                    type="password"
                    value={newPassword1}
                    onChange={(e) => setNewPassword1(e.target.value)}
                    required
                    className="block w-full rounded-md border-0 py-2.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="new_password2" className="block text-sm font-medium leading-6 text-gray-900">Confirm New Password</label>
                <div className="mt-2">
                  <input
                    id="new_password2"
                    name="new_password2"
                    type="password"
                    value={newPassword2}
                    onChange={(e) => setNewPassword2(e.target.value)}
                    required
                    className="block w-full rounded-md border-0 py-2.5 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-blue-600 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>
              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`w-full flex justify-center py-2.5 px-5 border border-transparent rounded-lg shadow-sm text-sm font-semibold text-white transition-colors duration-150 ease-in-out ${
                    isLoading ? 'bg-blue-400 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600'
                  }`}
                >
                  {isLoading ? 'Resetting...' : 'Reset Password'}
                </button>
              </div>
            </>
          )}
        </form>
         <p className="mt-8 text-center text-sm text-gray-500">
            Changed your mind?{' '}
            <Link to="/login" className="font-semibold leading-6 text-blue-600 hover:text-blue-500">
                Back to Login
            </Link>
        </p>
      </div>
    </div>
  );
};

export default PasswordResetConfirmPage;

